import apiClient from './api';

class InfomediaService {
  // ===== DETECCIÓN Y REGISTROS SIN MAPEAR =====

  /**
   * NUEVO: Verificación rápida de registros sin mapear (sin procesamiento pesado)
   */
  async checkUnmapped(ano, mes) {
    try {
      const response = await apiClient.get(`/infomedia/check-unmapped/${ano}/${mes}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error verificando registros sin mapear');
    }
  }

  /**
   * Detectar registros sin mapear por año/mes (OPERACIÓN PESADA)
   */
  async detectUnmapped(ano, mes) {
    try {
      const response = await apiClient.post(`/infomedia/detect-unmapped/${ano}/${mes}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error detectando registros sin mapear');
    }
  }

  /**
   * DEBUG: Verificar estructura de base de datos
   */
  async debugDatabase(ano, mes) {
    try {
      const response = await apiClient.get(`/infomedia/debug/${ano}/${mes}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error en debugging de base de datos');
    }
  }

  /**
   * Obtener registros sin mapear con filtros avanzados y paginación optimizada
   */
  async getUnmappedRecords(ano, mes, options = {}) {
    try {
      const { 
        prioridad, 
        medio, 
        item,
        sector,
        categoria,
        marca,
        search, 
        limit = 50, 
        offset = 0, 
        page, 
        sortBy = 'cantidadRegistros', 
        sortOrder = 'DESC' 
      } = options;
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        sortBy,
        sortOrder
      });
      
      // Usar page o offset
      if (page !== undefined) {
        params.append('page', page.toString());
      } else {
        params.append('offset', offset.toString());
      }
      
      if (prioridad && prioridad !== 'ALL') {
        params.append('prioridad', prioridad);
      }

      if (medio) {
        if (Array.isArray(medio) && medio.length > 0) {
          params.append('medio', medio.join(','));
        } else if (typeof medio === 'string' && medio !== 'ALL') {
          params.append('medio', medio);
        }
      }

      if (item && item !== 'ALL') {
        params.append('item', item);
      }

      if (sector) {
        if (Array.isArray(sector) && sector.length > 0) {
          params.append('sector', sector.join(','));
        } else if (typeof sector === 'string' && sector !== 'ALL') {
          params.append('sector', sector);
        }
      }

      if (categoria) {
        if (Array.isArray(categoria) && categoria.length > 0) {
          params.append('categoria', categoria.join(','));
        } else if (typeof categoria === 'string' && categoria !== 'ALL') {
          params.append('categoria', categoria);
        }
      }

      if (marca) {
        if (Array.isArray(marca) && marca.length > 0) {
          params.append('marca', marca.join(','));
        } else if (typeof marca === 'string' && marca !== 'ALL') {
          params.append('marca', marca);
        }
      }

      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      const response = await apiClient.get(
        `/infomedia/unmapped/${ano}/${mes}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo registros sin mapear');
    }
  }

  // ===== GESTIÓN DE MAPEOS =====

  /**
   * NUEVO: Obtener resumen del período actual (mapeos + sin mapear) - OPTIMIZADO
   */
  async getCurrentPeriod(ano, mes, options = {}) {
    try {
      const { 
        includeMapping = true, 
        includeUnmapped = true, 
        limit = 25, 
        offset = 0 
      } = options;
      
      const params = new URLSearchParams({
        includeMapping: includeMapping.toString(),
        includeUnmapped: includeUnmapped.toString(),
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await apiClient.get(
        `/infomedia/current-period/${ano}/${mes}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo período actual');
    }
  }

  /**
   * Obtener mapeos existentes
   */
  async getMappings(ano, mes, options = {}) {
    try {
      const { limit = 50, offset = 0 } = options;
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await apiClient.get(
        `/infomedia/mappings/${ano}/${mes}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo mapeos');
    }
  }

  /**
   * Crear mapeo individual
   */
  async createMapping(mappingData) {
    try {
      const response = await apiClient.post('/infomedia/mappings', mappingData);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error creando mapeo');
    }
  }

  /**
   * Actualizar mapeo existente
   */
  async updateMapping(id, updates) {
    try {
      const response = await apiClient.put(`/infomedia/mappings/${id}`, updates);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error actualizando mapeo');
    }
  }

  /**
   * Eliminar mapeo
   */
  async deleteMapping(id) {
    try {
      await apiClient.delete(`/infomedia/mappings/${id}`);
      return { success: true };
    } catch (error) {
      throw this.handleError(error, 'Error eliminando mapeo');
    }
  }

  /**
   * NUEVO: Crear mapeo rápido desde registro sin mapear
   */
  async createQuickMapping(quickMappingData) {
    try {
      const response = await apiClient.post('/infomedia/mappings/quick', quickMappingData);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error creando mapeo rápido');
    }
  }

  // ===== CARGA MASIVA =====

  /**
   * Cargar datos a staging
   */
  async loadToStaging(mappingsArray) {
    try {
      const response = await apiClient.post('/infomedia/staging/load', mappingsArray);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error cargando datos a staging');
    }
  }

  /**
   * Procesar staging a mapeos definitivos
   */
  async processStaging() {
    try {
      const response = await apiClient.post('/infomedia/staging/process');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error procesando staging');
    }
  }

  /**
   * Crear múltiples mapeos directamente
   */
  async createBulkMappings(mappingsArray) {
    try {
      const response = await apiClient.post('/infomedia/mappings/bulk', mappingsArray);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error creando mapeos masivos');
    }
  }

  // ===== DATOS CON MAPEO APLICADO =====

  /**
   * Obtener datos de FactInfomedia con mapeo aplicado
   */
  async getMappedData(ano, mes, options = {}) {
    try {
      const { soloMapeados = false, limit = 100, offset = 0 } = options;
      const params = new URLSearchParams({
        soloMapeados: soloMapeados.toString(),
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await apiClient.get(
        `/infomedia/data/${ano}/${mes}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo datos mapeados');
    }
  }

  // ===== REPORTES Y ESTADÍSTICAS =====

  /**
   * Obtener estadísticas de mapeo
   */
  async getStatistics(ano, mes) {
    try {
      const response = await apiClient.get(`/infomedia/stats/${ano}/${mes}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo estadísticas');
    }
  }

  /**
   * Obtener reporte resumen por segmentos
   */
  async getSummaryReport(ano, mes) {
    try {
      const response = await apiClient.get(`/infomedia/reports/summary/${ano}/${mes}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo reporte resumen');
    }
  }

  // ===== UTILIDADES =====

  /**
   * Health check del módulo
   */
  async healthCheck() {
    try {
      const response = await apiClient.get('/infomedia/health');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error en health check');
    }
  }

  /**
   * Validar datos de mapeo
   */
  validateMappingData(data) {
    const errors = [];
    
    if (!data.ano || data.ano < 2020 || data.ano > 2030) {
      errors.push('Año debe estar entre 2020 y 2030');
    }
    
    if (!data.mes || data.mes < 1 || data.mes > 12) {
      errors.push('Mes debe estar entre 1 y 12');
    }
    
    if (!data.medioOriginal?.trim()) {
      errors.push('Medio original es requerido');
    }
    
    if (!data.versionOriginal?.trim()) {
      errors.push('Versión original es requerida');
    }
    
    if (!data.campanaOriginal?.trim()) {
      errors.push('Campaña original es requerida');
    }
    
    if (!data.versionCliente?.trim()) {
      errors.push('Versión cliente es requerida');
    }
    
    if (!data.campanaCliente?.trim()) {
      errors.push('Campaña cliente es requerida');
    }

    return errors;
  }

  /**
   * Formatear datos para display
   */
  formatMappingForDisplay(mapping) {
    return {
      ...mapping,
      fechaCreacion: new Date(mapping.fechaCreacion).toLocaleDateString('es-ES'),
      fechaActualizacion: new Date(mapping.fechaActualizacion).toLocaleDateString('es-ES'),
      segmentoCliente: mapping.segmentoCliente || 'Sin Segmento',
      periodo: `${mapping.mes}/${mapping.ano}`,
    };
  }

  /**
   * Manejo de errores centralizado
   */
  handleError(error, defaultMessage) {
    let message = defaultMessage;
    if (error?.response?.data) {
      const data = error.response.data;
      if (typeof data === 'string') message = data;
      else if (data.message) message = Array.isArray(data.message) ? data.message.join(', ') : data.message;
      else if (data.error) message = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
    } else if (error?.message) {
      message = error.message;
    }
    
    return new Error(message);
  }

  /**
   * Obtener opciones de prioridad
   */
  getPriorityOptions() {
    return [
      { value: '', label: 'Todas las prioridades' },
      { value: 'ALTA', label: 'Alta Prioridad', color: 'danger' },
      { value: 'MEDIA', label: 'Media Prioridad', color: 'warning' },
      { value: 'BAJA', label: 'Baja Prioridad', color: 'info' },
    ];
  }

  /**
   * Obtener opciones de segmentos predefinidos
   */
  getSegmentOptions() {
    return [
      'Promocional',
      'Gaming Online',
      'Telecomunicaciones',
      'Servicios Financieros',
      'Retail',
      'Automotriz',
      'Salud y Belleza',
      'Entretenimiento',
      'Tecnología',
      'Educación',
      'Sin Segmento'
    ];
  }

  // ✅ ELIMINADO: getCategories - categorías vienen incluidas en getUnmappedRecords (como medios)

  /**
   * Carga masiva de mapeos desde archivo Excel
   */
  async uploadMassiveMapping(formData) {
    try {
      const response = await apiClient.post('/infomedia/upload-massive-mapping', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error en carga masiva de mapeos');
    }
  }

  async getCompetenciaDashboard(params = {}) {
    try {
      const qs = new URLSearchParams();
      qs.set('year', String(params.year ?? new Date().getFullYear()));
      if (params.month) qs.set('month', String(params.month));
      if (params.week) qs.set('week', String(params.week));
      if (params.grupoMedio) qs.set('grupoMedio', params.grupoMedio);
      if (params.categoria) qs.set('categoria', params.categoria);
      if (params.marca) qs.set('marca', params.marca);
      if (params.segmento) qs.set('segmento', params.segmento);
      if (params.bloque) qs.set('bloque', params.bloque);
      if (params.sector) qs.set('sector', params.sector);
      if (params.red) qs.set('red', params.red);
      if (params.genero) qs.set('genero', params.genero);
      if (params.medio) qs.set('medio', params.medio);
      if (params.variacionTipo) qs.set('variacionTipo', params.variacionTipo);
      if (params.companyId) qs.set('companyId', String(params.companyId));
      if (params.mercadoTotal) qs.set('mercadoTotal', 'true');
      const response = await apiClient.get(`/infomedia/competencia/dashboard?${qs.toString()}`);
      const body = response.data;
      return body?.data ?? body;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo dashboard de competencia');
    }
  }

  /** Datos planos para "Descargar Data" (Años/Meses/Categorías/Marcas/Emisoras/Inversión). */
  async getCompetenciaExport(params = {}) {
    try {
      const qs = new URLSearchParams();
      qs.set('year', String(params.year ?? new Date().getFullYear()));
      if (params.month) qs.set('month', String(params.month));
      if (params.week) qs.set('week', String(params.week));
      if (params.grupoMedio) qs.set('grupoMedio', params.grupoMedio);
      if (params.categoria) qs.set('categoria', params.categoria);
      if (params.marca) qs.set('marca', params.marca);
      if (params.segmento) qs.set('segmento', params.segmento);
      if (params.bloque) qs.set('bloque', params.bloque);
      if (params.companyId) qs.set('companyId', String(params.companyId));
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.mercadoTotal) qs.set('mercadoTotal', 'true');
      const response = await apiClient.get(`/infomedia/competencia/export?${qs.toString()}`);
      const body = response.data;
      return body?.data ?? body;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo datos de exportación');
    }
  }

  /** Narrativa competitiva con IA (clasificación de marcas + presión competitiva). */
  async getCompetenciaNarrativa(payload = {}) {
    try {
      const response = await apiClient.post('/infomedia/competencia/narrativa', payload);
      const body = response.data;
      return body?.data ?? body;
    } catch (error) {
      throw this.handleError(error, 'Error generando narrativa competitiva');
    }
  }

  /** Panorama Mercado Publicitario (ATL) — fuente MKP (General_tblComp_Next, INVERSION_ESTIMADA, sin TVPagada). */
  async getMercadoAtl(params = {}) {
    try {
      const response = await apiClient.get('/infomedia/mercado-atl', { params });
      const body = response.data;
      return body?.data ?? body;
    } catch (error) {
      throw this.handleError(error, 'Error cargando panorama de mercado ATL');
    }
  }

  async getMediaMix(ano, mes) {
    try {
      const response = await apiClient.get(`/infomedia/media-mix/${ano}/${mes}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo media mix 360');
    }
  }

  async getSegmentoAccounts() {
    try {
      const response = await apiClient.get('/infomedia/segmento-accounts');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo segmentos por cuenta');
    }
  }

  async updateSegmentoAccount(id, data) {
    try {
      const response = await apiClient.put(`/infomedia/segmento-accounts/${id}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error actualizando segmento por cuenta');
    }
  }

  async getSectores() {
    try {
      const response = await apiClient.get('/infomedia/sectores');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error obteniendo sectores');
    }
  }
}

const infomediaService = new InfomediaService();
export default infomediaService;